import { groupBy, orderBy, sortBy, values } from 'lodash';
import { useEffect, useState } from 'react';
import { useReactForm } from '@ninja/utilities';
import { QuoteSort } from '../../../../../common';
import { GroupedQuotes, InvestorImpactEnum, QuotePlanSchema } from '../../server/map-quotes';
import { QuoteFiltersFromUrl } from '../filter-schema/helpers';
import { QuoteFilters, useQuoteFilterSchema } from '../filter-schema/useQuoteFilterSchema';
import { applyQuoteFilter, filterWithPlanType, onSort } from './helpers';

export type QuoteListUrlParam = QuoteFilters & {
	planTypeId?: number;
	sortId?: string;
};

export const useQuoteFilterForm = ({
	originalQuotes,
	selectedPlanType,
	selectedSort,
	initialValuesFromUrl,
	onRouterReplace,
}: {
	originalQuotes: QuotePlanSchema[];
	selectedPlanType: number;
	selectedSort: QuoteSort;
	initialValuesFromUrl: QuoteFiltersFromUrl;
	onRouterReplace: (urlParam: QuoteListUrlParam) => void;
}) => {
	const initGroupingQuotes = () => {
		const groupByInsuranceCompanies = values(groupBy(originalQuotes, (quote) => quote.insuranceCompanyId));

		const defaultSorted = orderBy(groupByInsuranceCompanies, (group) => group[0].premiumAmount, 'asc');

		return defaultSorted.map((sortedPlan) => {
			return { selectedActivePlan: sortedPlan[0].planId, quotePlans: sortedPlan };
		});
	};

	const { schema } = useQuoteFilterSchema({ originalQuotes, initialValuesFromUrl });
	const [allQualifiedQuotes, setAllQualifiedQuotes] = useState<GroupedQuotes>(initGroupingQuotes());
	const filterQuoteByInvestorOrPartner = (isInvestorOrPartner: number, filterQuote: QuotePlanSchema[]) => {
		if (isInvestorOrPartner) {
			// LOGIC: Include UnImpacted, ImpactedButVisible and Exclude ImpactedButNotVisible
			return filterQuote.filter((q) => q.isInvestorPartnerOnly !== InvestorImpactEnum.ImpactedButNotVisible);
		} else {
			// LOGIC: Include UnImpacted, ImpactedButNotVisible and Exlude ImpactedButVisible
			return filterQuote.filter((q) => q.isInvestorPartnerOnly !== InvestorImpactEnum.ImpactedButVisible);
		}
	};

	const groupingFilteredQuotes = (filteredQuotes: QuotePlanSchema[]) => {
		addFiltersToUrl(selectedPlanType, selectedSort);

		const groupByInsuranceCompanies = values(
			sortBy(
				groupBy(filteredQuotes, (quote) => quote.insuranceCompanyId),
				(groupedIc) => filteredQuotes.indexOf(groupedIc[0])
			)
		);

		const sortedFilteredQuotes = groupByInsuranceCompanies.map((sortedPlan) => {
			return {
				selectedActivePlan:
					filteredQuotes.findIndex((q) => q.planId === sortedPlan[0].planId) !== -1 ? sortedPlan[0].planId : 0,
				quotePlans: orderBy(sortedPlan, (plan) => plan.premiumAmount, 'asc'),
			};
		});

		setAllQualifiedQuotes(sortedFilteredQuotes);
	};

	const applySortFilter = (selectedPlanType: number, selectedSort: QuoteSort, data?: QuotePlanSchema[]) => {
		let filteredQuotes = applyQuoteFilter(filterForm.watchValues(), data ?? originalQuotes);

		filteredQuotes = filterWithPlanType(selectedPlanType, filteredQuotes);

		filteredQuotes = onSort(selectedSort, filteredQuotes);

		filteredQuotes = filterQuoteByInvestorOrPartner(isInvestorOrPartner ? isInvestorOrPartner : 0, filteredQuotes);

		groupingFilteredQuotes(filteredQuotes);
	};

	const addFiltersToUrl = (planType: number, sort: QuoteSort) => {
		const filterValues = filterForm.watchValues();

		const urlParam = {
			planTypeId: planType,
			sortId: sort,
			...filterValues,
		};

		const transformedUrl: Record<string, string | number | string[] | number[] | QuoteSort> = {};

		for (const key in urlParam) {
			if (Object.prototype.hasOwnProperty.call(urlParam, key)) {
				const element = urlParam[key as keyof QuoteListUrlParam];

				if (element) {
					transformedUrl[key] = element;
				}
			}
		}

		onRouterReplace(transformedUrl);
	};

	const onReset = async () => {
		await filterForm.setFieldsValue({
			insurerCompanyId: undefined,
			opConsultationCoPaymentId: undefined,
			networkId: undefined,
			pharmacyCoPaymentId: undefined,
			keyBenefitId: undefined,
			diagnosticCoPaymentId: undefined,
		});

		applySortFilter(selectedPlanType, selectedSort);
	};

	const filterForm = useReactForm<QuoteFilters>({
		initialValues: schema.initialValues,
		onFormSubmit() {
			applySortFilter(selectedPlanType, selectedSort);
		},
		onFormReset: onReset,
	});

	const isInvestorOrPartner = filterForm.watchValues('investorOrPartner');

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { investorOrPartner, ...currentParams } = initialValuesFromUrl;
		let filteredQuotes: QuotePlanSchema[] = originalQuotes;

		filteredQuotes = filterQuoteByInvestorOrPartner(isInvestorOrPartner ? isInvestorOrPartner : 0, filteredQuotes);

		groupingFilteredQuotes(filteredQuotes);

		onRouterReplace({
			...(currentParams as QuoteFilters),
			...(isInvestorOrPartner ? { investorOrPartner: isInvestorOrPartner } : []),
		});
	}, [isInvestorOrPartner]);

	return {
		filterForm,
		filterFieldSchema: schema.fields,
		applySortFilter,
		allQualifiedQuotes,
		setAllQualifiedQuotes,
	};
};
